import { gql } from '@apollo/client';

/* ── the mutual fund research analyst — interactive sessions ─────────────── */

/**
 * Opens a session on one fund plan: today's research facts and the note from
 * the fund's last published report (`seed` "latest_report"; empty when the
 * fund has none) or an empty note ("empty"), which the analyst writes in chat
 * or the reader writes whole. `window`: quarterly | yearly | monthly. Long-running —
 * the facts take minutes the first time a fund is read in a day.
 */
export const START_MF_ANALYST_SESSION = gql`
  mutation StartMfResearchAnalystSession($securitySymbol: String!, $window: String = "quarterly", $seed: String = "latest_report") {
    startMfResearchAnalystSession(securitySymbol: $securitySymbol, window: $window, seed: $seed) {
      session { id securitySymbol researchWindow status currentRevisionId factsAsOf statements assumptions }
      revision { id revisionNumber headline changeSummary statements assumptions results conflicts adjustedGrades }
    }
  }
`;

/**
 * A NEW session generated from an earlier one — its final (or current) note,
 * edits, statements and assumptions on today's data. Optional `changes` make
 * revision 2; conflicting ones come back as CONFLICTS_NEED_DECISION.
 */
export const START_MF_ANALYST_UPDATE_SESSION = gql`
  mutation StartMfResearchAnalystUpdateSession($previousSessionId: Int!, $changes: JSON, $proceedWithConflicts: Boolean = false) {
    startMfResearchAnalystUpdateSession(previousSessionId: $previousSessionId, changes: $changes, proceedWithConflicts: $proceedWithConflicts) {
      status
      conflicts
      options
      session { id basedOnSessionId currentRevisionId factsAsOf }
      revision { id revisionNumber headline changeSummary statements assumptions results conflicts adjustedGrades }
    }
  }
`;

/**
 * Rewrites or extends one part of the note. `field`: headline | brief |
 * objective_and_style | market_context | what_would_change_it |
 * whats_working | whats_not_working | talking_points | performance_risks.
 * `mode`: replace | append. (The pillar summaries and single findings take
 * extra arguments; they are edited through the chat.)
 */
export const REVISE_MF_ANALYST_NOTE = gql`
  mutation ReviseMfResearchAnalystNote($sessionId: Int!, $field: String!, $text: String!, $mode: String! = "replace") {
    reviseMfResearchAnalystNote(sessionId: $sessionId, field: $field, text: $text, mode: $mode) {
      id
      revisionNumber
      changeSummary
    }
  }
`;

/**
 * The analyst's statements (verbatim) and the assumptions read from them.
 * APPLIED with the revision, or CONFLICTS_NEED_DECISION with conflicts and
 * options and nothing written — rerun with `proceedWithConflicts`.
 */
export const APPLY_MF_ANALYST_ASSUMPTIONS = gql`
  mutation ApplyMfResearchAnalystAssumptions($sessionId: Int!, $changes: JSON!, $proceedWithConflicts: Boolean = false) {
    applyMfResearchAnalystAssumptions(sessionId: $sessionId, changes: $changes, proceedWithConflicts: $proceedWithConflicts) {
      status
      conflicts
      options
      changeAsRead
      results
      revision { id revisionNumber changeSummary headline statements assumptions results conflicts adjustedGrades }
    }
  }
`;

/**
 * Re-reads today's facts under the same note, edits and assumptions, and lists
 * the passages quoting figures the new data no longer holds (rewritten with
 * reviseMfResearchAnalystNote).
 */
export const REFRESH_MF_ANALYST_DATA = gql`
  mutation RefreshMfResearchAnalystData($sessionId: Int!) {
    refreshMfResearchAnalystData(sessionId: $sessionId) {
      id
      revisionNumber
      changeSummary
      headline
      adjustedGrades
    }
  }
`;

/**
 * The whole note as one JSON object in the contract's output shape — it
 * becomes the new base note (earlier edits replaced, assumptions kept). Run
 * the note check after it.
 */
export const WRITE_MF_ANALYST_NOTE = gql`
  mutation WriteMfResearchAnalystNote($sessionId: Int!, $note: JSON!) {
    writeMfResearchAnalystNote(sessionId: $sessionId, note: $note) {
      id
      revisionNumber
      changeSummary
      headline
    }
  }
`;

/** Runs what the analyst proposed — finalise publishes the note as the fund's latest research and locks it. */
export const CONFIRM_MF_ANALYST_ACTION = gql`
  mutation ConfirmMfResearchAnalystAction($sessionId: Int!, $pendingActionId: Int!, $confirmationToken: String!) {
    confirmMfResearchAnalystAction(sessionId: $sessionId, pendingActionId: $pendingActionId, confirmationToken: $confirmationToken) {
      pendingActionId
      action
      status
      result
    }
  }
`;

export const REJECT_MF_ANALYST_ACTION = gql`
  mutation RejectMfResearchAnalystAction($sessionId: Int!, $pendingActionId: Int!, $note: String) {
    rejectMfResearchAnalystAction(sessionId: $sessionId, pendingActionId: $pendingActionId, note: $note) {
      pendingActionId
      action
      status
    }
  }
`;

/** A fresh one-time token for a proposed action — so one proposed before a reload can still be approved. */
export const ISSUE_MF_ANALYST_TOKEN = gql`
  mutation IssueMfResearchAnalystConfirmationToken($sessionId: Int!, $pendingActionId: Int!) {
    issueMfResearchAnalystConfirmationToken(sessionId: $sessionId, pendingActionId: $pendingActionId) {
      pendingActionId
      confirmationToken
    }
  }
`;

/** Brings an earlier revision (note, edits, assumptions) back as a NEW revision. */
export const REVERT_MF_ANALYST_REVISION = gql`
  mutation RevertMfResearchAnalystRevision($sessionId: Int!, $revisionNumber: Int!) {
    revertMfResearchAnalystRevision(sessionId: $sessionId, revisionNumber: $revisionNumber) {
      id
      revisionNumber
      changeSummary
    }
  }
`;

/** A presigned link to the interactive edition's PDF (with the labelled assumptions). */
export const DOWNLOAD_MF_ANALYST_REPORT = gql`
  mutation DownloadMfResearchAnalystReport($revisionId: Int!) {
    downloadMfResearchAnalystReport(revisionId: $revisionId) {
      revisionId
      s3Key
      url
    }
  }
`;

/** Stops the session (owner or research lead). Revisions, the published research and PDFs stay. */
export const CLOSE_MF_ANALYST_SESSION = gql`
  mutation CloseMfResearchAnalystSession($sessionId: Int!) {
    closeMfResearchAnalystSession(sessionId: $sessionId) {
      id
      status
      finalRevisionId
      mfResearchId
      reviewId
    }
  }
`;
